import React, { useState, useEffect } from "react";
import { partnershipService } from "../services/apiService";
import {
  FiEye,
  FiCheck,
  FiX,
  FiAlertTriangle,
  FiMessageCircle,
  FiFilter,
  FiRefreshCw,
  FiMail,
  FiPhone,
  FiBriefcase,
  FiGlobe,
} from "react-icons/fi";

const Partnerships = () => {
  const [partnerships, setPartnerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedPartnership, setSelectedPartnership] = useState(null);
  const [filters, setFilters] = useState({
    status: "all",
    organizationType: "all",
    partnershipType: "all",
  });
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    reviewed: 0,
    inDiscussion: 0,
    approved: 0,
    rejected: 0,
  });

  useEffect(() => {
    fetchPartnerships();
    fetchStats();
  }, []);

  const fetchPartnerships = async () => {
    try {
      setLoading(true);
      const response = await partnershipService.getAll();
      setPartnerships(response.data || []);
      setError("");
    } catch (err) {
      setError(err.message || "Failed to fetch partnerships");
      console.error("Error fetching partnerships:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await partnershipService.getStats();
      setStats(response.data || {});
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await partnershipService.update(id, { status });
      await fetchPartnerships();
      await fetchStats();
      setSelectedPartnership(null);
      alert(`Partnership status updated to ${status}`);
    } catch (err) {
      alert(err.message || "Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this partnership inquiry?")) {
      return;
    }

    try {
      await partnershipService.delete(id);
      await fetchPartnerships();
      await fetchStats();
      setSelectedPartnership(null);
      alert("Partnership deleted successfully");
    } catch (err) {
      alert(err.message || "Failed to delete partnership");
    }
  };

  const filteredPartnerships = partnerships.filter((p) => {
    if (filters.status !== "all" && p.status !== filters.status) return false;
    if (filters.organizationType !== "all" && p.organizationType !== filters.organizationType)
      return false;
    if (filters.partnershipType !== "all" && p.partnershipType !== filters.partnershipType)
      return false;
    return true;
  });

  const getStatusBadge = (status) => {
    const badges = {
      pending: "bg-yellow-100 text-yellow-800",
      reviewed: "bg-blue-100 text-blue-800",
      "in-discussion": "bg-purple-100 text-purple-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
    };
    return badges[status] || "bg-gray-100 text-gray-800";
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      low: "bg-gray-100 text-gray-600",
      medium: "bg-blue-100 text-blue-600",
      high: "bg-orange-100 text-orange-600",
      urgent: "bg-red-100 text-red-600",
    };
    return badges[priority] || "bg-gray-100 text-gray-600";
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-medium text-gray-900 mb-2">Partnership Inquiries</h1>
          <p className="text-gray-600">Manage organization partnership requests</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="text-2xl font-medium text-gray-900">{stats.total || 0}</div>
            <div className="text-sm text-gray-600">Total</div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
            <div className="text-2xl font-medium text-yellow-800">{stats.pending || 0}</div>
            <div className="text-sm text-yellow-700">Pending</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div className="text-2xl font-medium text-blue-800">{stats.reviewed || 0}</div>
            <div className="text-sm text-blue-700">Reviewed</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
            <div className="text-2xl font-medium text-purple-800">{stats.inDiscussion || 0}</div>
            <div className="text-sm text-purple-700">In Discussion</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <div className="text-2xl font-medium text-green-800">{stats.approved || 0}</div>
            <div className="text-sm text-green-700">Approved</div>
          </div>
          <div className="bg-red-50 rounded-lg p-4 border border-red-200">
            <div className="text-2xl font-medium text-red-800">{stats.rejected || 0}</div>
            <div className="text-sm text-red-700">Rejected</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg p-4 mb-6 border border-gray-200">
          <div className="flex items-center gap-2 mb-3">
            <FiFilter className="text-gray-600" />
            <h3 className="font-semibold text-gray-900">Filters</h3>
            <button
              onClick={() => {
                setFilters({ status: "all", organizationType: "all", partnershipType: "all" });
              }}
              className="ml-auto text-sm text-green-600 hover:text-green-700 flex items-center gap-1"
            >
              <FiRefreshCw className="w-4 h-4" />
              Reset
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="in-discussion">In Discussion</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Organization Type
              </label>
              <select
                value={filters.organizationType}
                onChange={(e) => setFilters({ ...filters, organizationType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                <option value="corporate">Corporate</option>
                <option value="ngo">NGO</option>
                <option value="government">Government</option>
                <option value="foundation">Foundation</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Partnership Type
              </label>
              <select
                value={filters.partnershipType}
                onChange={(e) => setFilters({ ...filters, partnershipType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="all">All Partnerships</option>
                <option value="sponsorship">Sponsorship</option>
                <option value="csr">CSR Initiative</option>
                <option value="technology">Technology</option>
                <option value="research">Research</option>
                <option value="funding">Funding</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <FiAlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Partnerships Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPartnerships.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                      No partnership inquiries found
                    </td>
                  </tr>
                ) : (
                  filteredPartnerships.map((partnership) => (
                    <tr key={partnership._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-start gap-2">
                          <FiBriefcase className="text-gray-400 flex-shrink-0 mt-1" />
                          <div>
                            <div className="font-medium text-gray-900">
                              {partnership.organizationName}
                            </div>
                            <div className="text-sm text-gray-500 capitalize">
                              {partnership.organizationType}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">
                            {partnership.contactPerson}
                          </div>
                          <div className="text-gray-500 flex items-center gap-1">
                            <FiMail className="w-3 h-3" />
                            {partnership.email}
                          </div>
                          {partnership.phone && (
                            <div className="text-gray-500 flex items-center gap-1">
                              <FiPhone className="w-3 h-3" />
                              {partnership.phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm capitalize">{partnership.partnershipType}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(
                            partnership.status
                          )}`}
                        >
                          {partnership.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityBadge(
                            partnership.priority
                          )}`}
                        >
                          {partnership.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatDate(partnership.createdAt)}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => setSelectedPartnership(partnership)}
                          className="text-green-600 hover:text-green-700 font-medium text-sm"
                        >
                          <FiEye className="inline w-4 h-4 mr-1" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedPartnership && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
              <h2 className="text-2xl font-medium text-gray-900">Partnership Details</h2>
              <button
                onClick={() => setSelectedPartnership(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Status and Priority */}
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <span
                    className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadge(
                      selectedPartnership.status
                    )}`}
                  >
                    {selectedPartnership.status}
                  </span>
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                  <span
                    className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getPriorityBadge(
                      selectedPartnership.priority
                    )}`}
                  >
                    {selectedPartnership.priority}
                  </span>
                </div>
              </div>

              {/* Organization Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organization Name
                  </label>
                  <p className="text-gray-900 font-medium">
                    {selectedPartnership.organizationName}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organization Type
                  </label>
                  <p className="text-gray-900 capitalize">{selectedPartnership.organizationType}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Person
                  </label>
                  <p className="text-gray-900">{selectedPartnership.contactPerson}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <p className="text-gray-900 flex items-center gap-1">
                    <FiGlobe className="w-4 h-4 text-gray-400" />
                    {selectedPartnership.country}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <p className="text-gray-900 flex items-center gap-1">
                    <FiMail className="w-4 h-4 text-gray-400" />
                    <a
                      href={`mailto:${selectedPartnership.email}`}
                      className="text-green-600 hover:underline"
                    >
                      {selectedPartnership.email}
                    </a>
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <p className="text-gray-900 flex items-center gap-1">
                    <FiPhone className="w-4 h-4 text-gray-400" />
                    <a
                      href={`tel:${selectedPartnership.phone}`}
                      className="text-green-600 hover:underline"
                    >
                      {selectedPartnership.phone}
                    </a>
                  </p>
                </div>
              </div>

              {/* Partnership Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Partnership Interest
                </label>
                <p className="text-gray-900 capitalize">{selectedPartnership.partnershipType}</p>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-gray-900 whitespace-pre-wrap">{selectedPartnership.message}</p>
                </div>
              </div>

              {/* Admin Notes */}
              {selectedPartnership.adminNotes && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <FiMessageCircle className="inline w-4 h-4 mr-1" />
                    Admin Notes
                  </label>
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <p className="text-gray-900 whitespace-pre-wrap">
                      {selectedPartnership.adminNotes}
                    </p>
                  </div>
                </div>
              )}

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4 text-sm text-gray-500">
                <div>
                  <span className="font-medium">Submitted:</span>{" "}
                  {formatDate(selectedPartnership.createdAt)}
                </div>
                <div>
                  <span className="font-medium">Last Updated:</span>{" "}
                  {formatDate(selectedPartnership.updatedAt)}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                {selectedPartnership.status === "pending" && (
                  <>
                    <button
                      onClick={() =>
                        handleUpdateStatus(selectedPartnership._id, "reviewed")
                      }
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <FiCheck className="w-4 h-4" />
                      Mark as Reviewed
                    </button>
                    <button
                      onClick={() =>
                        handleUpdateStatus(selectedPartnership._id, "in-discussion")
                      }
                      className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <FiMessageCircle className="w-4 h-4" />
                      Start Discussion
                    </button>
                  </>
                )}
                {(selectedPartnership.status === "reviewed" ||
                  selectedPartnership.status === "in-discussion") && (
                  <>
                    <button
                      onClick={() =>
                        handleUpdateStatus(selectedPartnership._id, "approved")
                      }
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <FiCheck className="w-4 h-4" />
                      Approve
                    </button>
                    <button
                      onClick={() =>
                        handleUpdateStatus(selectedPartnership._id, "rejected")
                      }
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <FiX className="w-4 h-4" />
                      Reject
                    </button>
                  </>
                )}
                <button
                  onClick={() => handleDelete(selectedPartnership._id)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Partnerships;
